// Управляващ: избор на един или няколко майстори за конкретна нередност.

import React, { useEffect, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { api } from '../../api';
import { showError, showSuccess } from '../../toast';
import { HANDYMAN_SPECIALTIES } from '../../constants';
import { REGIONS } from '../../geo';
import { Button, Card, COLORS, Input, Label, Muted, Screen, SectionLabel, Select, Title } from '../../ui';
import { t } from '../../i18n';

const ALL_SPECIALTIES = 'Всички специалности';

export default function HandymenPickScreen({ route, navigation }: any) {
  const { issueId } = route.params;
  // Категория, подадена отвън (напр. „Почистване" след напуснал наемател)
  const preset = route.params.presetSpecialty as string | undefined;
  // Областта на имота (подава се от екрана на нередността/имота); иначе първата
  const presetRegion = route.params.region as string | undefined;
  const [handymen, setHandymen] = useState<any[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState('');
  const [area, setArea] = useState(presetRegion && REGIONS.includes(presetRegion) ? presetRegion : REGIONS[0]);
  const [specialty, setSpecialty] = useState(preset ?? ALL_SPECIALTIES);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('area', area);
    if (specialty !== ALL_SPECIALTIES) params.set('specialty', specialty);
    const qs = params.toString();
    api(`/jobs/handymen${qs ? `?${qs}` : ''}`)
      .then((r) => setHandymen(r.handymen))
      .catch((e) => showError(e.message));
  }, [area, specialty]);

  const toggle = (id: string) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Един бутон: запитването отива до ВСИЧКИ майстори в областта на имота
  // (по избраната специалност, ако има). Офертите се състезават.
  const broadcast = async () => {
    setLoading(true);
    try {
      const r = await api(`/jobs/issues/${issueId}/requests/broadcast`, {
        body: { specialty, ...(message ? { message } : {}) },
      });
      showSuccess(t('Запитването е изпратено до {n} майстори „{specialty}“ в областта. Ще получите офертите им.', { n: r.sent, specialty }));
      navigation.goBack();
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const send = async () => {
    setLoading(true);
    try {
      await api(`/jobs/issues/${issueId}/requests`, {
        body: { handymanIds: [...selected], message: message || undefined },
      });
      showSuccess(t('Запитването е изпратено до {n} майстор(и).', { n: selected.size }));
      navigation.goBack();
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Screen>
      <Title>{t('Избор на майстор')}</Title>
      <Card style={{ borderColor: COLORS.primary, borderWidth: 1.5 }}>
        <Label>{t('Най-бързият начин')}</Label>
        <Muted>
          {t('Избери каква работа е нужна и прати запитването до всички такива майстори в областта на имота — те се състезават с оферти, ти избираш най-добрата.')}
        </Muted>
        <Select
          placeholder={t('Каква работа е нужна?')}
          options={HANDYMAN_SPECIALTIES}
          value={specialty === ALL_SPECIALTIES ? null : specialty}
          onChange={setSpecialty}
        />
        <Button
          title={specialty === ALL_SPECIALTIES ? t('📣 Първо избери категория') : t('📣 До всички „{specialty}“ в областта', { specialty })}
          onPress={broadcast}
          disabled={specialty === ALL_SPECIALTIES}
          loading={loading}
        />
      </Card>
      <SectionLabel>{t('Или избери ръчно')}</SectionLabel>
      <Select placeholder={t('Област')} options={REGIONS} value={area} onChange={setArea} />
      <View style={{ height: 8 }} />
      <Select
        placeholder={t('Специалност')}
        options={[ALL_SPECIALTIES, ...HANDYMAN_SPECIALTIES]}
        value={specialty}
        onChange={setSpecialty}
      />
      <View style={{ height: 8 }} />
      {handymen.length === 0 && (
        <Card>
          <Muted>{t('Няма регистрирани майстори за тази област.')}</Muted>
        </Card>
      )}
      {handymen.map((h) => (
        <Pressable key={h.id} onPress={() => toggle(h.id)}>
          <Card style={selected.has(h.id) ? { borderColor: COLORS.primary, borderWidth: 2 } : {}}>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <View
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  borderWidth: 2,
                  marginRight: 10,
                  borderColor: selected.has(h.id) ? COLORS.primary : COLORS.muted,
                  backgroundColor: selected.has(h.id) ? COLORS.primary : 'transparent',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {selected.has(h.id) && <Text style={{ color: '#fff', fontSize: 14 }}>✓</Text>}
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 16, fontWeight: '600', color: COLORS.text }}>
                  {h.firstName} {h.lastName}
                  {h.handymanProfile?.ratingAvg ? ` · ★ ${Number(h.handymanProfile.ratingAvg).toFixed(1)}` : ''}
                </Text>
                <Muted>
                  {h.handymanProfile?.specialties?.join(', ')} · {h.handymanProfile?.workArea}
                </Muted>
              </View>
            </View>
          </Card>
        </Pressable>
      ))}

      <Label>{t('Съобщение (по избор)')}</Label>
      <Input value={message} onChangeText={setMessage} placeholder={t('Кога можете да минете?')} />
      <Button
        title={t('Изпрати запитване ({n})', { n: selected.size })}
        onPress={send}
        disabled={selected.size === 0}
        loading={loading}
      />
    </Screen>
  );
}
